import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { Section, SectionHeading, Reveal } from "../lib/ui";
import { SERVICES } from "../config";

const ABOUT_IMG = "https://images.unsplash.com/photo-1531819177115-428566ccfb50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function About() {
  return (
    <Section id="about" className="bg-ink">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <SectionHeading
            overline="Our Story"
            title={<>Built on experience, <span className="gold-text">not theory</span>.</>}
          />
          <Reveal delay={0.1}>
            <p className="mt-8 text-silver/70 font-body font-light leading-relaxed">
              Arco Orbis2 didn't start in a boardroom. It started with two friends — both British military
              veterans — who spent more than twenty years making decisions in places where getting security
              wrong was never an option.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 text-silver/70 font-body font-light leading-relaxed">
              Over those years we kept noticing the same thing: organisations were handed generic security
              advice that looked impressive on paper but fell apart in the real world. So we built the firm we
              always wished existed — one that treats every client's situation as its own problem to solve,
              rather than reaching for an off-the-shelf answer.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-silver/70 font-body font-light leading-relaxed">
              Today we help organisations, individuals, buildings and facilities understand the risks they
              actually face — and do something practical about them. Whether it's a security question, a
              difficult journey, a crisis plan or the technical detail behind a building's protection, you'll
              get honest, considered advice. Never off-the-shelf, and never more complicated than it needs to be.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-px bg-white/5 hairline rounded-xl overflow-hidden">
            {["Security", "Travel Risk", "Crisis Risk", "Risk Assessment", "Governance", "Technical Advisory"].map((t, i) => (
              <Reveal key={t} delay={0.05 * i} className="bg-ink px-5 py-4">
                <span className="font-body text-sm text-silver/80">{t}</span>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1} className="relative">
            <div className="absolute -inset-4 rounded-2xl bg-gold/5 blur-2xl" />
            <div className="relative rounded-2xl overflow-hidden hairline">
              <img src={ABOUT_IMG} alt="Global city skyline" className="w-full h-[420px] md:h-[520px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline text-gold mb-2">Since Day One</div>
                <p className="font-display font-light text-2xl text-white">Real experience. Honest advice.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

export function Marquee() {
  const items = ["Security", "Risk", "Resilience", "Strategy", "Technology", "Global Reach"];
  return (
    <div className="relative border-y border-white/10 bg-ink-surface overflow-hidden py-8" data-testid="marquee">
      <div className="flex whitespace-nowrap animate-marquee">
        {[...items, ...items].map((t, i) => (
          <span key={i} className="mx-8 font-display font-light text-3xl md:text-5xl text-silver/25 inline-flex items-center gap-8">
            {t} <span className="text-gold/50 text-2xl">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ServiceCard({ service, index, open, onToggle }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      className={`group hairline rounded-xl bg-ink-surface/60 hover:border-gold/30 transition-colors duration-500 scroll-mt-28 ${
        open ? "border-gold/40" : ""
      }`}
      id={`svc-${index}`}
      data-testid={`service-card-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-6 flex items-start justify-between gap-4"
        data-testid={`service-toggle-${index}`}
      >
        <div>
          <span className="font-body text-xs text-gold/60 tracking-widest">{String(index + 1).padStart(2, "0")}</span>
          <h3 className="mt-2 font-display font-light text-xl leading-snug text-white group-hover:text-gold-light transition-colors duration-300">
            {service.title}
          </h3>
        </div>
        <span className="mt-1 shrink-0 h-8 w-8 rounded-full border border-white/15 flex items-center justify-center text-gold group-hover:border-gold/50 transition-colors">
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6">
              <p className="text-silver/70 font-body font-light text-sm leading-relaxed">{service.summary}</p>
              {service.process && (
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {service.process.map((step, i) => (
                    <React.Fragment key={step}>
                      <span className="rounded-full bg-gold/10 border border-gold/20 px-3 py-1 text-xs font-body text-gold-light">{step}</span>
                      {i < service.process.length - 1 && <ArrowRight size={12} className="text-silver/40" />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              {service.points && (
                <ul className="mt-5 grid sm:grid-cols-2 gap-y-2 gap-x-6">
                  {service.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-silver/70 font-body">
                      <span className="h-1 w-1 rounded-full bg-gold" /> {p}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function Services() {
  const [open, setOpen] = useState(0);
  const { hash } = useLocation();
  useEffect(() => {
    const m = hash.match(/^#svc-(\d+)$/);
    if (m) setOpen(Number(m[1]));
  }, [hash]);
  return (
    <Section id="services" className="bg-ink">
      <SectionHeading
        overline="Our Services"
        title={<>Bespoke security, risk and <span className="silver-text">technical advisory</span>.</>}
        kicker="Bespoke security consultancy, risk management and technical advisory solutions designed around your requirements."
      />
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.title} service={s} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
        ))}
      </div>
    </Section>
  );
}
